import { syncedGet, syncedSet } from '../data/serverStore';
import React, { useState, useMemo } from 'react';
import { PARAM_DEFS, getDefaultUnit, getParamMeta } from '../data/params';

// ── Storage keys ──────────────────────────────────────────────────────────────
const AREAS_KEY  = 'sl_param_areas';   // full area definitions (built-in overrides + custom)
const META_KEY   = 'sl_param_meta';    // { paramName: { unit, method, spec } } overrides

function loadAreas() {
  try { return JSON.parse((syncedGet(AREAS_KEY, null) !== null ? JSON.stringify(syncedGet(AREAS_KEY, null)) : null) || 'null'); }
  catch { return null; }
}
function saveAreas(v) { syncedSet(AREAS_KEY, v); }
function loadMeta() {
  try { return JSON.parse((syncedGet(META_KEY, null) !== null ? JSON.stringify(syncedGet(META_KEY, null)) : null) || '{}'); }
  catch { return {}; }
}
function saveMeta(v) { syncedSet(META_KEY, v); }

const BUILTIN_LABELS = {
  dissolved: 'Dissolved Metals',
  total:     'Total Metals',
  nonmetals: 'Non-Metals',
  rare:      'Rare Earth Metals',
  fuel:      'Fuel',
  gc:        'GC / PCBs',
};

// ── Build initial areas from PARAM_DEFS if no saved data ─────────────────────
function initAreas() {
  const saved = loadAreas();
  if (saved) return saved;
  const areas = {};
  Object.entries(BUILTIN_LABELS).forEach(([key, label]) => {
    areas[key] = { label, params: (PARAM_DEFS[key] || []).map(p => ({ name: p })) };
  });
  return areas;
}

// ── Public helpers used by SampleSetup ───────────────────────────────────────
export function getMergedParams(areaKey) {
  const areas = loadAreas();
  if (areas && areas[areaKey]) return areas[areaKey].params.map(p => p.name || p);
  return (PARAM_DEFS[areaKey] || []);
}

export function getAllAreas() {
  const areas = initAreas();
  return Object.entries(areas).map(([key, val]) => ({
    key, label: val.label,
    params: val.params,
    isBuiltinKey: !!BUILTIN_LABELS[key],
  }));
}

// ── Get effective meta for a param (saved override > built-in default) ────────
function getEffectiveMeta(paramName) {
  const saved = loadMeta();
  const builtin = getParamMeta(paramName);
  const baseUnit = saved[paramName]?.unit ?? getDefaultUnit(paramName);
  const solidFallback = baseUnit === 'mg/L' ? 'mg/kg'
    : baseUnit === 'ug/L' ? 'ug/kg' : baseUnit;
  return {
    unitLiquid:   saved[paramName]?.unitLiquid   ?? baseUnit,
    methodLiquid: saved[paramName]?.methodLiquid ?? saved[paramName]?.method ?? builtin.method,
    unitSolid:    saved[paramName]?.unitSolid    ?? solidFallback,
    methodSolid:  saved[paramName]?.methodSolid  ?? saved[paramName]?.method ?? builtin.method,
    specLiquid:   saved[paramName]?.specLiquid ?? saved[paramName]?.spec ?? builtin.spec,
    specSolid:    saved[paramName]?.specSolid  ?? saved[paramName]?.spec ?? builtin.spec,
    unitTclp:     saved[paramName]?.unitTclp    ?? 'mg/L',
    methodTclp:   saved[paramName]?.methodTclp  ?? saved[paramName]?.methodLiquid ?? saved[paramName]?.method ?? builtin.method,
    specTclp:     saved[paramName]?.specTclp    ?? '',
  };
}

// ── ParamRow component ────────────────────────────────────────────────────────
function ParamRow({ param, idx, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const meta = getEffectiveMeta(param.name || param);
  const name = param.name || param;

  const startEdit = () => {
    setForm({ name, unitLiquid:meta.unitLiquid, methodLiquid:meta.methodLiquid,
      unitSolid:meta.unitSolid, methodSolid:meta.methodSolid, specLiquid:meta.specLiquid,
      specSolid:meta.specSolid, unitTclp:meta.unitTclp, methodTclp:meta.methodTclp, specTclp:meta.specTclp });
    setEditing(true);
  };

  const handleSave = () => {
    if (!form.name.trim()) return;
    onSave(name, form);
    setEditing(false);
  };

  if (editing) {
    return (
      <tr style={{background:'#f0f6ff'}}>
        <td style={{padding:'4px 8px',color:'#aaa',fontSize:11,textAlign:'center'}}>{idx+1}</td>
        <td style={{padding:'3px 6px'}}>
          <input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))}
            style={{width:'100%',border:'1px solid #1e3060',borderRadius:3,
              padding:'3px 6px',fontSize:11,outline:'none'}}/>
        </td>
        <td style={{padding:'3px 6px'}}>
          <input value={form.unitLiquid} onChange={e=>setForm(p=>({...p,unitLiquid:e.target.value}))}
            placeholder="mg/L"
            style={{width:'100%',border:'1px solid #cdd3e4',borderRadius:3,
              padding:'3px 6px',fontSize:11,outline:'none',textAlign:'center'}}/>
        </td>
        <td style={{padding:'3px 6px'}}>
          <input value={form.methodLiquid} onChange={e=>setForm(p=>({...p,methodLiquid:e.target.value}))}
            placeholder="e.g. D-93"
            style={{width:'100%',border:'1px solid #cdd3e4',borderRadius:3,
              padding:'3px 6px',fontSize:11,outline:'none',fontFamily:'monospace'}}/>
        </td>
        <td style={{padding:'3px 6px'}}>
          <input value={form.unitSolid} onChange={e=>setForm(p=>({...p,unitSolid:e.target.value}))}
            placeholder="mg/kg" style={{width:'100%',border:'1px solid #cdd3e4',borderRadius:3,
              padding:'3px 6px',fontSize:11,outline:'none',textAlign:'center'}}/>
        </td>
        <td style={{padding:'3px 6px'}}>
          <input value={form.methodSolid} onChange={e=>setForm(p=>({...p,methodSolid:e.target.value}))}
            placeholder="e.g. SW-846 3050B" style={{width:'100%',border:'1px solid #cdd3e4',borderRadius:3,
              padding:'3px 6px',fontSize:11,outline:'none',fontFamily:'monospace'}}/>
        </td>
        <td style={{padding:'3px 6px'}}>
          <input value={form.specLiquid} onChange={e=>setForm(p=>({...p,specLiquid:e.target.value}))}
            placeholder="Liquid specification"
            style={{width:'100%',border:'1px solid #cdd3e4',borderRadius:3,
              padding:'3px 6px',fontSize:11,outline:'none'}}/>
        </td>
        <td style={{padding:'3px 6px'}}><input value={form.specSolid} onChange={e=>setForm(p=>({...p,specSolid:e.target.value}))} placeholder="Solid specification" style={{width:'100%',border:'1px solid #cdd3e4',borderRadius:3,padding:'3px 6px',fontSize:11}}/></td>
        <td style={{padding:'3px 6px'}}><input value={form.unitTclp} onChange={e=>setForm(p=>({...p,unitTclp:e.target.value}))} placeholder="mg/L" style={{width:'100%',border:'1px solid #cdd3e4',borderRadius:3,padding:'3px 6px',fontSize:11,textAlign:'center'}}/></td>
        <td style={{padding:'3px 6px'}}><input value={form.methodTclp} onChange={e=>setForm(p=>({...p,methodTclp:e.target.value}))} placeholder="EPA 6010D" style={{width:'100%',border:'1px solid #cdd3e4',borderRadius:3,padding:'3px 6px',fontSize:11,fontFamily:'monospace'}}/></td>
        <td style={{padding:'3px 6px'}}><input value={form.specTclp} onChange={e=>setForm(p=>({...p,specTclp:e.target.value}))} placeholder="TCLP regulatory limit" style={{width:'100%',border:'1px solid #cdd3e4',borderRadius:3,padding:'3px 6px',fontSize:11}}/></td>
        <td style={{padding:'3px 6px',display:'flex',gap:4,justifyContent:'center'}}>
          <button className="btn btn-sm btn-primary" onClick={handleSave}>✓ Save</button>
          <button className="btn btn-sm" onClick={()=>setEditing(false)}>✕</button>
        </td>
      </tr>
    );
  }

  return (
    <tr style={{borderBottom:'1px solid #f0f2f8'}}
      onMouseEnter={e=>e.currentTarget.style.background='#fafbff'}
      onMouseLeave={e=>e.currentTarget.style.background='white'}>
      <td style={{padding:'5px 8px',color:'#aaa',fontSize:11,textAlign:'center'}}>{idx+1}</td>
      <td style={{padding:'5px 8px',fontSize:12,fontWeight:500,color:'#1e3060'}}>{name}</td>
      <td style={{padding:'5px 8px',fontSize:11,textAlign:'center',
        color:'#7788aa',fontWeight:600}}>{meta.unitLiquid||'—'}</td>
      <td style={{padding:'5px 8px',fontSize:11,fontFamily:'monospace',
        color:'#1a4060'}}>{meta.methodLiquid||'—'}</td>
      <td style={{padding:'5px 8px',fontSize:11,textAlign:'center',
        color:'#8a5b28',fontWeight:600}}>{meta.unitSolid||'—'}</td>
      <td style={{padding:'5px 8px',fontSize:11,fontFamily:'monospace',
        color:'#70451f'}}>{meta.methodSolid||'—'}</td>
      <td style={{padding:'5px 8px',fontSize:11,color:'#666'}}>{meta.specLiquid||'—'}</td>
      <td style={{padding:'5px 8px',fontSize:11,color:'#666'}}>{meta.specSolid||'—'}</td>
      <td style={{padding:'5px 8px',fontSize:11,textAlign:'center',color:'#25745a',fontWeight:600}}>{meta.unitTclp||'—'}</td>
      <td style={{padding:'5px 8px',fontSize:11,fontFamily:'monospace',color:'#165b47'}}>{meta.methodTclp||'—'}</td>
      <td style={{padding:'5px 8px',fontSize:11,color:'#165b47'}}>{meta.specTclp||'—'}</td>
      <td style={{padding:'5px 8px',display:'flex',gap:4,justifyContent:'center'}}>
        <button className="btn btn-sm" onClick={startEdit} title="Edit">✏️</button>
        <button className="btn btn-sm" style={{color:'#b83232'}}
          onClick={()=>onDelete(name)} title="Delete">🗑</button>
      </td>
    </tr>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function ParamManager({ toast }) {
  const [areas,        setAreas]        = useState(initAreas);
  const [selectedKey,  setSelectedKey]  = useState(null);
  const [search,       setSearch]       = useState('');
  const [showAddArea,  setShowAddArea]  = useState(false);
  const [newAreaLabel, setNewAreaLabel] = useState('');
  const [showAddParam, setShowAddParam] = useState(false);
  const [newParam,     setNewParam]     = useState({name:'',unitLiquid:'',methodLiquid:'',specLiquid:'',unitSolid:'',methodSolid:'',specSolid:'',unitTclp:'mg/L',methodTclp:'',specTclp:''});
  const [renamingArea, setRenamingArea] = useState(null);
  const [confirmDel,   setConfirmDel]   = useState(null);

  const persist = (updated) => { setAreas(updated); saveAreas(updated); };

  const area = selectedKey ? areas[selectedKey] : null;

  // ── Area actions ──────────────────────────────────────────────────────────
  const addArea = () => {
    if (!newAreaLabel.trim()) { toast('error','Enter area name'); return; }
    const key = 'area_' + Date.now();
    const updated = { ...areas, [key]: { label: newAreaLabel.trim(), params: [] } };
    persist(updated);
    setNewAreaLabel(''); setShowAddArea(false);
    setSelectedKey(key);
    toast('success', `"${newAreaLabel.trim()}" created`);
  };

  const renameArea = (key, label) => {
    if (!label.trim()) return;
    const updated = { ...areas, [key]: { ...areas[key], label: label.trim() } };
    persist(updated);
    setRenamingArea(null);
    toast('success', 'Area renamed');
  };

  const deleteArea = (key) => {
    const updated = { ...areas };
    delete updated[key];
    persist(updated);
    if (selectedKey === key) setSelectedKey(null);
    setConfirmDel(null);
    toast('info', 'Area deleted');
  };

  // ── Param actions ─────────────────────────────────────────────────────────
  const addParam = () => {
    if (!newParam.name.trim()) { toast('error','Enter parameter name'); return; }
    if (!selectedKey || !area) return;
    if (area.params.find(p => (p.name||p) === newParam.name.trim())) {
      toast('error', 'Parameter already exists'); return;
    }
    const updated = { ...areas };
    updated[selectedKey] = {
      ...area,
      params: [...area.params, { name: newParam.name.trim() }]
    };
    // Save meta
    if (newParam.unitLiquid || newParam.methodLiquid || newParam.specLiquid || newParam.unitSolid || newParam.methodSolid || newParam.specSolid || newParam.unitTclp || newParam.methodTclp || newParam.specTclp) {
      const meta = loadMeta();
      meta[newParam.name.trim()] = {
        unitLiquid:newParam.unitLiquid, methodLiquid:newParam.methodLiquid,
        unitSolid:newParam.unitSolid, methodSolid:newParam.methodSolid, specLiquid:newParam.specLiquid,
        specSolid:newParam.specSolid, unitTclp:newParam.unitTclp, methodTclp:newParam.methodTclp, specTclp:newParam.specTclp
      };
      saveMeta(meta);
    }
    persist(updated);
    setNewParam({name:'',unitLiquid:'',methodLiquid:'',specLiquid:'',unitSolid:'',methodSolid:'',specSolid:'',unitTclp:'mg/L',methodTclp:'',specTclp:''});
    setShowAddParam(false);
    toast('success', `"${newParam.name.trim()}" added`);
  };

  const saveParam = (oldName, form) => {
    if (!selectedKey || !area) return;
    const updated = { ...areas };
    updated[selectedKey] = {
      ...area,
      params: area.params.map(p => (p.name||p) === oldName ? { name: form.name.trim() } : p)
    };
    // Save meta override
    const meta = loadMeta();
    if (oldName !== form.name.trim()) delete meta[oldName];
    meta[form.name.trim()] = { unitLiquid:form.unitLiquid, methodLiquid:form.methodLiquid,
      unitSolid:form.unitSolid, methodSolid:form.methodSolid, specLiquid:form.specLiquid,
      specSolid:form.specSolid, unitTclp:form.unitTclp, methodTclp:form.methodTclp, specTclp:form.specTclp };
    saveMeta(meta);
    persist(updated);
    toast('success', 'Parameter saved');
  };

  const deleteParam = (paramName) => {
    if (!selectedKey || !area) return;
    const updated = { ...areas };
    updated[selectedKey] = {
      ...area,
      params: area.params.filter(p => (p.name||p) !== paramName)
    };
    persist(updated);
    toast('info', `"${paramName}" removed`);
  };

  // Filter
  const filtered = useMemo(() => {
    if (!area) return [];
    const params = area.params;
    if (!search) return params;
    return params.filter(p => (p.name||p).toLowerCase().includes(search.toLowerCase()));
  }, [area, search]);

  const totalParams = Object.values(areas).reduce((a,ar)=>a+(ar.params?.length||0),0);

  return (
    <div className="content" style={{display:'flex',gap:14,
      height:'calc(100vh - 180px)',overflow:'hidden'}}>

      {/* ── Left: Area list ── */}
      <div style={{width:250,flexShrink:0,display:'flex',flexDirection:'column',gap:8}}>
        <div className="panel" style={{flex:1,overflow:'hidden',display:'flex',flexDirection:'column'}}>
          <div className="panel-header">
            🧪 Testing Areas
            <span style={{marginLeft:'auto',fontSize:10,color:'#aac4e0',fontWeight:400}}>
              {Object.keys(areas).length} areas · {totalParams} params
            </span>
          </div>
          <div style={{flex:1,overflowY:'auto'}}>
            {Object.entries(areas).map(([key, ar]) => {
              const isActive = selectedKey === key;
              const isBuiltin = !!BUILTIN_LABELS[key];
              return (
                <div key={key}
                  style={{borderBottom:'1px solid #f0f2f8',
                    background:isActive?'#ddeeff':'white',
                    borderLeft:`3px solid ${isActive?'#1e3060':'transparent'}`,
                    transition:'background 0.1s'}}>
                  {renamingArea === key ? (
                    <div style={{padding:'6px 10px',display:'flex',gap:4}}>
                      <input defaultValue={ar.label} autoFocus
                        onBlur={e=>renameArea(key,e.target.value)}
                        onKeyDown={e=>{
                          if(e.key==='Enter') renameArea(key,e.target.value);
                          if(e.key==='Escape') setRenamingArea(null);
                        }}
                        style={{flex:1,border:'1px solid #1e3060',borderRadius:3,
                          padding:'3px 6px',fontSize:12,outline:'none'}}/>
                    </div>
                  ) : (
                    <div style={{padding:'8px 10px',cursor:'pointer',
                      display:'flex',alignItems:'center',gap:6}}
                      onClick={()=>{setSelectedKey(key);setSearch('');}}>
                      <div style={{flex:1}}>
                        <div style={{fontSize:12,fontWeight:600,
                          color:isActive?'#1e3060':'#333'}}>{ar.label}</div>
                        <div style={{fontSize:10,color:'#888',marginTop:1}}>
                          {ar.params?.length||0} parameters
                          {!isBuiltin && <span style={{marginLeft:4,fontSize:9,
                            background:'#9c27b0',color:'white',
                            borderRadius:4,padding:'1px 4px'}}>custom</span>}
                        </div>
                      </div>
                      {isActive && (
                        <div style={{display:'flex',gap:2}}>
                          <button onClick={e=>{e.stopPropagation();setRenamingArea(key);}}
                            style={{background:'none',border:'none',cursor:'pointer',
                              fontSize:11,color:'#888',padding:'1px 3px'}}>✏️</button>
                          <button onClick={e=>{e.stopPropagation();setConfirmDel({key,ar});}}
                            style={{background:'none',border:'none',cursor:'pointer',
                              fontSize:11,color:'#b83232',padding:'1px 3px'}}>🗑</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{padding:10,borderTop:'1px solid #e0e4ec',flexShrink:0}}>
            {showAddArea ? (
              <div style={{display:'flex',flexDirection:'column',gap:6}}>
                <input value={newAreaLabel} onChange={e=>setNewAreaLabel(e.target.value)}
                  autoFocus onKeyDown={e=>e.key==='Enter'&&addArea()}
                  placeholder="e.g. Radiological..."
                  style={{border:'1px solid #cdd3e4',borderRadius:4,padding:'6px 8px',
                    fontSize:12,outline:'none',width:'100%',boxSizing:'border-box'}}/>
                <div style={{display:'flex',gap:6}}>
                  <button className="btn btn-primary" style={{flex:1,fontSize:11}}
                    onClick={addArea}>✓ Create</button>
                  <button className="btn" style={{fontSize:11}}
                    onClick={()=>{setShowAddArea(false);setNewAreaLabel('');}}>✕</button>
                </div>
              </div>
            ) : (
              <button className="btn btn-primary" style={{width:'100%',fontSize:11}}
                onClick={()=>setShowAddArea(true)}>
                ＋ New Testing Area
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Right: Parameters ── */}
      <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        {!area ? (
          <div className="panel" style={{flex:1,display:'flex',
            alignItems:'center',justifyContent:'center'}}>
            <div style={{textAlign:'center',color:'#999'}}>
              <div style={{fontSize:36,marginBottom:10}}>🧪</div>
              <div style={{fontSize:14,fontWeight:600,marginBottom:6}}>
                Select a Testing Area
              </div>
              <div style={{fontSize:12}}>
                Choose an area from the left to view and manage its parameters
              </div>
            </div>
          </div>
        ) : (
          <div className="panel" style={{flex:1,overflow:'hidden',
            display:'flex',flexDirection:'column'}}>
            <div className="panel-header">
              {area.label}
              <span style={{marginLeft:'auto',fontSize:11,fontWeight:400,color:'#aac4e0'}}>
                {area.params?.length||0} parameters
              </span>
            </div>

            {/* Toolbar */}
            <div style={{padding:'8px 12px',borderBottom:'1px solid #e0e4ec',
              background:'#f8f9ff',display:'flex',gap:8,alignItems:'center',flexShrink:0}}>
              <button className="btn btn-primary" style={{fontSize:11}}
                onClick={()=>setShowAddParam(v=>!v)}>
                {showAddParam ? '✕ Cancel' : '＋ Add Parameter'}
              </button>
              <div style={{flex:1}}/>
              <input value={search} onChange={e=>setSearch(e.target.value)}
                placeholder="Search parameters..."
                style={{width:200,border:'1px solid #cdd3e4',borderRadius:4,
                  padding:'5px 10px',fontSize:11,outline:'none'}}/>
              {search && <button className="btn btn-sm" onClick={()=>setSearch('')}>✕</button>}
            </div>

            {/* Add parameter form */}
            {showAddParam && (
              <div style={{padding:'10px 12px',background:'#f0f6ff',
                borderBottom:'1px solid #c8d8f0',flexShrink:0}}>
                <div style={{display:'grid',
                  gridTemplateColumns:'1.6fr .7fr 1fr 1fr .7fr 1fr 1fr .7fr 1fr 1fr auto',gap:6,
                  alignItems:'end'}}>
                  <div>
                    <div style={{fontSize:10,fontWeight:700,color:'#555',
                      marginBottom:3,textTransform:'uppercase'}}>
                      Parameter Name *
                    </div>
                    <input value={newParam.name}
                      onChange={e=>setNewParam(p=>({...p,name:e.target.value}))}
                      onKeyDown={e=>e.key==='Enter'&&addParam()}
                      placeholder="e.g. Radon-222"
                      autoFocus
                      style={{width:'100%',border:'1px solid #1e3060',borderRadius:4,
                        padding:'6px 8px',fontSize:12,outline:'none',
                        boxSizing:'border-box'}}/>
                  </div>
                  <div>
                    <div style={{fontSize:10,fontWeight:700,color:'#555',
                      marginBottom:3,textTransform:'uppercase'}}>Liquid Unit</div>
                    <input value={newParam.unitLiquid}
                      onChange={e=>setNewParam(p=>({...p,unitLiquid:e.target.value}))}
                      placeholder="mg/L"
                      style={{width:'100%',border:'1px solid #cdd3e4',borderRadius:4,
                        padding:'6px 8px',fontSize:12,outline:'none',
                        boxSizing:'border-box',textAlign:'center'}}/>
                  </div>
                  <div>
                    <div style={{fontSize:10,fontWeight:700,color:'#555',
                      marginBottom:3,textTransform:'uppercase'}}>Liquid Method</div>
                    <input value={newParam.methodLiquid}
                      onChange={e=>setNewParam(p=>({...p,methodLiquid:e.target.value}))}
                      placeholder="e.g. EPA 903.0"
                      style={{width:'100%',border:'1px solid #cdd3e4',borderRadius:4,
                        padding:'6px 8px',fontSize:12,outline:'none',
                        boxSizing:'border-box',fontFamily:'monospace'}}/>
                  </div>
                  <div><div style={{fontSize:10,fontWeight:700,color:'#555',marginBottom:3,textTransform:'uppercase'}}>Solid Unit</div>
                    <input value={newParam.unitSolid} onChange={e=>setNewParam(p=>({...p,unitSolid:e.target.value}))}
                      placeholder="mg/kg" style={{width:'100%',border:'1px solid #cdd3e4',borderRadius:4,padding:'6px 8px',fontSize:12,boxSizing:'border-box',textAlign:'center'}}/>
                  </div>
                  <div><div style={{fontSize:10,fontWeight:700,color:'#555',marginBottom:3,textTransform:'uppercase'}}>Solid Method</div>
                    <input value={newParam.methodSolid} onChange={e=>setNewParam(p=>({...p,methodSolid:e.target.value}))}
                      placeholder="e.g. SW-846 3050B" style={{width:'100%',border:'1px solid #cdd3e4',borderRadius:4,padding:'6px 8px',fontSize:12,boxSizing:'border-box',fontFamily:'monospace'}}/>
                  </div>
                  <div>
                    <div style={{fontSize:10,fontWeight:700,color:'#555',
                      marginBottom:3,textTransform:'uppercase'}}>
                      Liquid Specification
                    </div>
                    <input value={newParam.specLiquid}
                      onChange={e=>setNewParam(p=>({...p,specLiquid:e.target.value}))}
                      placeholder="Liquid limit"
                      style={{width:'100%',border:'1px solid #cdd3e4',borderRadius:4,
                        padding:'6px 8px',fontSize:12,outline:'none',
                        boxSizing:'border-box'}}/>
                  </div>
                  <div><div style={{fontSize:10,fontWeight:700,color:'#555',marginBottom:3,textTransform:'uppercase'}}>Solid Specification</div>
                    <input value={newParam.specSolid} onChange={e=>setNewParam(p=>({...p,specSolid:e.target.value}))} placeholder="Solid limit" style={{width:'100%',border:'1px solid #cdd3e4',borderRadius:4,padding:'6px 8px',fontSize:12,boxSizing:'border-box'}}/>
                  </div>
                  <div><div style={{fontSize:10,fontWeight:700,color:'#17634d',marginBottom:3,textTransform:'uppercase'}}>TCLP Unit</div>
                    <input value={newParam.unitTclp} onChange={e=>setNewParam(p=>({...p,unitTclp:e.target.value}))} placeholder="mg/L" style={{width:'100%',border:'1px solid #8fc7b3',borderRadius:4,padding:'6px 8px',fontSize:12,boxSizing:'border-box',textAlign:'center'}}/>
                  </div>
                  <div><div style={{fontSize:10,fontWeight:700,color:'#17634d',marginBottom:3,textTransform:'uppercase'}}>TCLP Method</div>
                    <input value={newParam.methodTclp} onChange={e=>setNewParam(p=>({...p,methodTclp:e.target.value}))} placeholder="EPA 6010D" style={{width:'100%',border:'1px solid #8fc7b3',borderRadius:4,padding:'6px 8px',fontSize:12,boxSizing:'border-box',fontFamily:'monospace'}}/>
                  </div>
                  <div><div style={{fontSize:10,fontWeight:700,color:'#17634d',marginBottom:3,textTransform:'uppercase'}}>TCLP Specification</div>
                    <input value={newParam.specTclp} onChange={e=>setNewParam(p=>({...p,specTclp:e.target.value}))} placeholder="Regulatory limit" style={{width:'100%',border:'1px solid #8fc7b3',borderRadius:4,padding:'6px 8px',fontSize:12,boxSizing:'border-box'}}/>
                  </div>
                  <button className="btn btn-primary"
                    style={{padding:'7px 16px',fontSize:12,whiteSpace:'nowrap'}}
                    onClick={addParam}>
                    ＋ Add
                  </button>
                </div>
              </div>
            )}

            {/* Parameter table */}
            <div style={{flex:1,overflowY:'auto'}}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{width:40,textAlign:'center'}}>#</th>
                    <th>Parameter Name</th>
                    <th style={{width:75,textAlign:'center'}}>Liquid Unit</th>
                    <th style={{width:110,textAlign:'center'}}>Liquid Method</th>
                    <th style={{width:75,textAlign:'center'}}>Solid Unit</th>
                    <th style={{width:110,textAlign:'center'}}>Solid Method</th>
                    <th style={{width:120}}>Liquid Specification</th>
                    <th style={{width:120}}>Solid Specification</th>
                    <th style={{width:75,textAlign:'center'}}>TCLP Unit</th>
                    <th style={{width:110,textAlign:'center'}}>TCLP Method</th>
                    <th style={{width:120}}>TCLP Specification</th>
                    <th style={{width:90,textAlign:'center'}}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((param, idx) => (
                    <ParamRow
                      key={param.name||param}
                      param={param}
                      idx={area.params.indexOf(param)}
                      onSave={saveParam}
                      onDelete={deleteParam}
                    />
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={12} className="text-center text-muted"
                      style={{padding:24}}>
                      {search ? 'No parameters match your search'
                        : 'No parameters yet — click ＋ Add Parameter above'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div style={{padding:'6px 14px',borderTop:'1px solid #e0e4ec',
              background:'#f8f9ff',fontSize:11,color:'#888',
              display:'flex',gap:16,flexShrink:0}}>
              <span>{area.params?.length||0} total parameters</span>
              {search && <span style={{color:'#1e3060'}}>
                Showing {filtered.length} matching "{search}"
              </span>}
              <span style={{marginLeft:'auto',color:'#aaa',fontSize:10}}>
                Changes take effect immediately in Sample Setup
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Confirm delete area */}
      {confirmDel && (
        <div className="modal-overlay" onClick={()=>setConfirmDel(null)}>
          <div className="modal" style={{width:400}} onClick={e=>e.stopPropagation()}>
            <div className="modal-header" style={{background:'#b83232'}}>
              <h2>🗑 Delete Area</h2>
              <button className="modal-close" onClick={()=>setConfirmDel(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p>Delete <strong>"{confirmDel.ar.label}"</strong> and its {confirmDel.ar.params?.length||0} parameters?</p>
              <p style={{marginTop:8,fontSize:11,color:'#888'}}>
                Existing samples using these parameters are not affected.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-danger"
                onClick={()=>deleteArea(confirmDel.key)}>
                🗑 Delete
              </button>
              <button className="btn" onClick={()=>setConfirmDel(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
